/**
 * Document raw-text page. GET /documents/:id (COP-117 detail) already returns
 * `extractedText`; this page is the human reader over that field — a full-
 * width page rather than a modal because the text can be megabytes and wants
 * the viewport, browser find, and a stable URL. Mounted at BOTH
 * /admin/documents/show/:id/text and /me/documents/show/:id/text — one
 * component, no fork (COP-117 D-14); the hub `read`-gates the document.
 */
import { useState } from 'react';
import { useShow } from '@refinedev/core';
import { Link, useParams } from 'react-router';
import type { DocumentDetail } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { DocumentStatusBadge } from './List';
import { NO_ACCESS_COPY } from './Show';

type Readiness = DocumentDetail['readiness'];

/** Statuses before extraction has produced (or failed to produce) text. */
const IN_PIPELINE = new Set<Readiness['status']>(['queued', 'extracting', 'chunking', 'embedding']);

function missingTextCopy(readiness: Readiness): string {
  if (IN_PIPELINE.has(readiness.status)) return 'Text extraction has not finished.';
  return readiness.failureReason ?? 'No extracted text is stored for this document.';
}

/** Pure text view (exported seam): the text verbatim, or why there is none. */
export function ExtractedTextView({
  extractedText,
  readiness,
}: {
  extractedText: string | null | undefined;
  readiness: Readiness;
}) {
  if (!extractedText) {
    return <p className="text-sm text-muted-foreground">{missingTextCopy(readiness)}</p>;
  }
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">{extractedText}</pre>
  );
}

export function DocumentsText() {
  // Resource + id pinned explicitly so the read works from both shells — see
  // the same note in DocumentsShow.
  const { id } = useParams();
  const { query } = useShow<DocumentDetail>({ resource: 'documents', id });
  const doc = query.data?.data;
  const [copied, setCopied] = useState(false);

  if (query.isLoading) return <div>Loading…</div>;
  if (!doc) return <Alert>{NO_ACCESS_COPY}</Alert>;

  const text = doc.extractedText ?? '';
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{doc.filename}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <DocumentStatusBadge status={doc.status} />
            <span>
              Extracted text · {text.length.toLocaleString()} characters
              {doc.pageCount ? ` · ${doc.pageCount} ${doc.pageCount === 1 ? 'page' : 'pages'}` : ''}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={() => void copy()} disabled={!text}>
            {copied ? 'Copied' : 'Copy text'}
          </Button>
          {/* relative="path": one URL segment up (the detail page) in both
              shells — the default route-relative ".." would leave the admin
              documents list instead, since show/:id and show/:id/text are
              sibling routes there. */}
          <Link to=".." relative="path" className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>
            Back to document
          </Link>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <ExtractedTextView extractedText={doc.extractedText} readiness={doc.readiness} />
        </CardContent>
      </Card>
    </div>
  );
}
